import {Link} from 'react-router-dom';
import {ReactableColumnDef, InfoBox, Reactable} from '@deviceinsight/ng-ui-components';

import {
	DataProcessingDefinitionValidation,
	PaginationSettings,
	Pageable,
	AssetGroup
} from '@deviceinsight/ng-ui-api-client';

import TooltipBubble from './TooltipBubble';
import warning_icon from '../../assets/icon_warning_triangle.svg';
import success_icon from '../../assets/icon_success.svg';

const columnDefs: Array<ReactableColumnDef<DataProcessingDefinitionValidation>> = [
	{
		name: 'name',
		label: 'Asset Name',
		sortable: true,
		render: (item) => (
			<Link to={`/assets/${item.id}/datapoints`} target="_blank">
				{item.name}
			</Link>
		)
	},
	{
		name: 'missing_datapoints',
		label: 'Missing Datapoints',
		sortable: true,
		render: ({compatibleDatapoints, missingDatapoints}: DataProcessingDefinitionValidation) => {
			return missingDatapoints.length === 1 ? (
				missingDatapoints.toString()
			) : (
				<TooltipBubble availableDatapoints={compatibleDatapoints} missingDatapoints={missingDatapoints} />
			);
		}
	}
];

const getSuccessTranslations = (assetGroupName: string) => ({
	id: 'assets.detail.dataProcessingDefinition.impactinformation.header.noConflicts',
	assetGroupName,
	defaultValue: 'This KPI is compatible with every Asset of the Asset Group {{assetGroupName}}'
});

const getWarningTranslations = (
	assetGroupName: string,
	numberOfConflicts: number,
	dataProcessingDefinitionName: string
) => ({
	id: 'assets.detail.dataProcessingDefinition.impactinformation.header.conflicts',
	numberOfConflicts,
	assetGroupName,
	dataProcessingDefinitionName,
	defaultValue: `
		The following {{numberOfConflicts}} assets from Asset Group {{assetGroupName}} do not have all neccessary
		datapoints needed to calculate the KPI {{dataProcessingDefinitionName}}. The KPI will
		not be processable or editable within these assets. In order to make the KPI compatible with the
		assets, the listed datapoints have to be added.
	`
});

type Props = {
	assetGroup: AssetGroup;
	dataProcessingDefinitionName: string;
	assetValidations: Pageable<DataProcessingDefinitionValidation> | null | undefined;
	numberOfConflicts: number;
	paginationSettings: PaginationSettings;
	onChangePage: (page: number) => void;
	onSort: (property: string, direction: 'ASC' | 'DESC') => void;
};

const ImpactInformation = ({
	assetGroup,
	dataProcessingDefinitionName,
	assetValidations,
	numberOfConflicts,
	paginationSettings,
	onChangePage,
	onSort
}: Props) => {
	const successTranslations = getSuccessTranslations(assetGroup.displayName ?? assetGroup.name);
	const warningTranslations = getWarningTranslations(
		assetGroup.displayName ?? assetGroup.name,
		numberOfConflicts,
		dataProcessingDefinitionName
	);
	return numberOfConflicts === 0 ? (
		<InfoBox type="success" icon={success_icon} message={successTranslations} />
	) : (
		<InfoBox type="warning" icon={warning_icon} message={warningTranslations}>
			<div className="impact-information-assets-table">
				<Reactable
					columnDefs={columnDefs}
					isLoading={false}
					pageable={assetValidations}
					uniqueItemKey={(item) => item.id}
					paginationSettings={paginationSettings}
					onChangePage={onChangePage}
					onSort={onSort}
				/>
			</div>
		</InfoBox>
	);
};

export default ImpactInformation;
